import React from "react";

export interface ToastItem {
  id: string;
  kind: "success" | "error";
  message: string;
}

export interface ToastProps {
  toasts: ToastItem[];
}

/**
 * Toast — stateless top-center display for the admin console toast queue.
 * Auto-dismiss is handled by the queue owner (AdminPage). Container uses
 * pointer-events-none so it doesn't intercept clicks on the page below;
 * individual toasts re-enable pointer events. Animation reuses the slideDown
 * keyframe defined in web/index.html via Tailwind's arbitrary-value escape
 * `animate-[slideDown_150ms_ease-out]`.
 */
const Toast: React.FC<ToastProps> = ({ toasts }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => {
        const isSuccess = toast.kind === "success";
        const borderClass = isSuccess ? "border-primary/30" : "border-error/30";
        const iconName = isSuccess ? "check_circle" : "error";
        const iconColor = isSuccess ? "text-primary" : "text-error";

        return (
          <div
            key={toast.id}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg pointer-events-auto text-sm font-body min-w-[280px] max-w-sm animate-[slideDown_150ms_ease-out] bg-surface-container text-on-surface ${borderClass}`}
            role="status"
            aria-live="polite"
          >
            <span className={`material-symbols-outlined text-base ${iconColor}`}>
              {iconName}
            </span>
            <span>{toast.message}</span>
          </div>
        );
      })}
    </div>
  );
};

export default Toast;
